import {TData, TPayload} from 'src/components/List';
import {TItemId} from 'src/constants';
import {EWebNotificationType, isMatchNotificationQuery} from 'src/containers/Notifications';
import {CACHE_LIFETIME, ECacheTag, listenerMiddleware, rootApi} from 'src/models';
import {mapNullToUndefined, mapUndefinedToNull} from 'src/utils';

import {TCycles, TFlow, TFlowStep, TSchedule} from './types';

type TFlowMutation = {
  name: string;
  description: string;
  onlyOnes: boolean;
  subjectId?: number;
  startAt?: Date;
  endAt?: Date;
  schedule?: TSchedule;
};

enum EFlowErrorCodes {
  CYCLES_FOUND = 'step-cycles-found',
}

type TCyclesError = {
  title: EFlowErrorCodes.CYCLES_FOUND;
  args: {
    cycles: TCycles;
  };
};

type TReportGeneration = {
  name: string;
};

enum EReportGenerationStatus {
  ACCEPTED = 'ACCEPTED',
}

type TIntegration = {
  isActive: boolean;
};

const prepareFlowStepsForSave = (steps: TFlowStep[]) =>
  mapUndefinedToNull(steps.map((step) => ({...step, meta: JSON.stringify(step.meta)})));

const flowApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    createFlow: builder.mutation<TFlow, TFlowMutation>({
      query: (data) => ({
        method: 'POST',
        url: '/flow/',
        data: mapUndefinedToNull(data),
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.FLOW],
    }),
    saveFlow: builder.mutation<TFlow, {id: TFlow['id']; data: TFlowMutation}>({
      query: ({id, data}) => ({
        method: 'PUT',
        url: `/flow/${id}`,
        data: mapUndefinedToNull(data),
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'List:notification.saveSuccess',
        },
      }),
      invalidatesTags: (_, __, {id}) => [{type: ECacheTag.FLOW, id}],
    }),
    activateFlow: builder.mutation<TFlow, TFlow['id']>({
      query: (id) => ({
        method: 'POST',
        url: `/flow/${id}/activate`,
        meta: {
          isShowError: true,
        },
      }),
      invalidatesTags: (_, __, id) => [{type: ECacheTag.FLOW, id}],
    }),
    deactivateFlow: builder.mutation<TFlow, TFlow['id']>({
      query: (id) => ({
        method: 'POST',
        url: `/flow/${id}/deactivate`,
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'Triggers:notification.deactivateSuccess',
        },
      }),
      invalidatesTags: (_, __, id) => [{type: ECacheTag.FLOW, id}],
    }),
    copyFlow: builder.mutation<TFlow, TFlow['id']>({
      query: (id) => ({
        method: 'POST',
        url: `/flow/${id}/copy`,
        meta: {
          isShowError: true,
        },
      }),
      invalidatesTags: [ECacheTag.FLOW],
    }),
    deleteFlow: builder.mutation<TFlow, TFlow['id']>({
      query: (id) => ({
        method: 'DELETE',
        url: `/flow/${id}/delete`,
        meta: {
          isShowError: true,
          errorMessageKey: 'List:errors.failedToDeleteItem',
          isShowSuccess: true,
          successMessageKey: 'List:notification.deleteSuccess',
        },
      }),
      invalidatesTags: [ECacheTag.FLOW],
    }),
    getFlows: builder.query<TData<TFlow>, TPayload>({
      query: ({tenantId, ...data}) => ({
        method: 'POST',
        url: tenantId ? `/flow/list/tenant/${tenantId}` : '/flow/list',
        data,
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: (result) => {
        if (!result) {
          return [ECacheTag.FLOW];
        }

        return [ECacheTag.FLOW, ...result.content.map(({id}) => ({id, type: ECacheTag.FLOW}))];
      },
    }),
    getExternalTriggerFlows: builder.query<TFlow[], void>({
      query: () => ({
        method: 'POST',
        url: '/flow/filter',
        data: {
          statuses: ['ACTIVE'],
          conditionType: 'TRIGGER_FLOW_CONDITION',
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: (result) => {
        if (!result) {
          return [ECacheTag.FLOW];
        }

        return [ECacheTag.FLOW, ...result.map(({id}) => ({id, type: ECacheTag.FLOW}))];
      },
    }),
    getFlow: builder.query<TFlow, {flowId: TFlow['id']; tenantId?: TItemId}>({
      query: ({flowId, tenantId}) => ({
        method: 'GET',
        url: tenantId ? `/flow/${flowId}/tenant/${tenantId}` : `/flow/${flowId}`,
      }),
      providesTags: (_, __, {flowId}) => [{id: flowId, type: ECacheTag.FLOW}],
    }),
    getFlowSteps: builder.query<TFlowStep[], {flowId: TFlow['id']; tenantId?: TItemId}>({
      query: ({flowId, tenantId}) => ({
        method: 'GET',
        url: tenantId ? `/flow/${flowId}/steps/tenant/${tenantId}` : `/flow/${flowId}/steps`,
      }),
      providesTags: [ECacheTag.FLOW_STEPS],
      transformResponse: (data: Array<TFlowStep & {meta: string}>) =>
        mapNullToUndefined(
          data.map((step) => ({
            ...step,
            meta: JSON.parse(step.meta || '{}'),
          })),
        ),
    }),
    validateFlowSteps: builder.mutation<
      {isValid: boolean},
      {flowId: TFlow['id']; tenantId?: TItemId; steps: TFlowStep[]}
    >({
      query: ({flowId, tenantId, steps}) => ({
        method: 'POST',
        data: prepareFlowStepsForSave(steps),
        url: tenantId
          ? `/flow/${flowId}/steps/validate/tenant/${tenantId}`
          : `/flow/${flowId}/steps/validate`,
      }),
    }),
    saveFlowSteps: builder.mutation<
      TFlowStep[],
      {id: TFlow['id']; steps: TFlowStep[]; ignoreCycles?: boolean}
    >({
      query: ({id, steps, ignoreCycles}) => ({
        method: 'POST',
        url: `/flow/${id}/steps`,
        data: prepareFlowStepsForSave(steps),
        params: {ignoreCycles},
        meta: {
          isShowSuccess: true,
          successMessageKey: 'Triggers:notification.saveSuccess',
        },
      }),
      invalidatesTags: [ECacheTag.FLOW_STEPS],
    }),
    generateFlowReport: builder.mutation<
      {status: EReportGenerationStatus; name: string},
      {flowId: TFlow['id']; locale: string}
    >({
      query: ({flowId, locale}) => ({
        method: 'POST',
        url: '/report/flow/download',
        data: {
          flowId,
          locale,
        },
        meta: {
          isShowError: true,
        },
      }),
    }),
    getFlowReportGenerations: builder.query<TReportGeneration[], void>({
      query: () => ({
        method: 'GET',
        url: '/report/flow/downloads',
        meta: {isShowError: true},
      }),
      providesTags: [ECacheTag.FLOW_MESSAGE_REPORT_GENERATIONS],
    }),
    getChatCenterIntegration: builder.query<TIntegration, TItemId>({
      query: (subjectId) => ({
        method: 'GET',
        url: `/chat-center/integration/subject/${subjectId}/status`,
      }),
    }),
    getJivoIntegration: builder.query<TIntegration, TItemId>({
      query: (subjectId) => ({
        method: 'GET',
        url: `/jivo/integration/subject/${subjectId}/status`,
      }),
    }),
    getCallbackIntegration: builder.query<TIntegration, TItemId>({
      query: (subjectId) => ({
        method: 'GET',
        url: `/callback/in-message/subject/${subjectId}/status`,
      }),
    }),
  }),
});

listenerMiddleware.startListening({
  matcher: flowApi.endpoints.generateFlowReport.matchFulfilled,
  effect: ({payload}, listenerApi) => {
    if (payload.status === EReportGenerationStatus.ACCEPTED) {
      listenerApi.dispatch(
        flowApi.util.updateQueryData('getFlowReportGenerations', undefined, (draft) =>
          draft ? [...draft, {name: payload.name}] : [{name: payload.name}],
        ),
      );
    }
  },
});

listenerMiddleware.startListening({
  matcher: isMatchNotificationQuery(
    EWebNotificationType.FlowMessageReportSuccess,
    EWebNotificationType.FlowMessageReportFailed,
  ),
  effect: (_, listenerApi) => {
    listenerApi.dispatch(flowApi.util.invalidateTags([ECacheTag.FLOW_MESSAGE_REPORT_GENERATIONS]));
  },
});

export const {
  useGetFlowsQuery,
  useGetExternalTriggerFlowsQuery,
  useGetFlowQuery,
  useGetFlowStepsQuery,
  useCreateFlowMutation,
  useSaveFlowStepsMutation,
  useActivateFlowMutation,
  useDeactivateFlowMutation,
  useCopyFlowMutation,
  useDeleteFlowMutation,
  useSaveFlowMutation,
  useGenerateFlowReportMutation,
  useGetFlowReportGenerationsQuery,
  useValidateFlowStepsMutation,
  useGetJivoIntegrationQuery,
  useGetCallbackIntegrationQuery,
  useGetChatCenterIntegrationQuery,
  endpoints,
} = flowApi;

export {TFlowMutation, TCyclesError, EFlowErrorCodes, TReportGeneration};
